import { useState, useCallback, useRef, useEffect } from 'react';
import { sessionAPI } from '../../services/api';

/**
 * useSessionAdmin - Anonymous session analytics state & actions.
 *
 * Handlers use `useCallback` with EMPTY deps and read fresh state via refs,
 * because the AdminContext freezes function references in `useMemo(..., [])`.
 * Without this, clicks on the bulk-delete button would always see an empty
 * selection (stale closure) and silently return.
 */
export const useSessionAdmin = () => {
  const [sessionStats, setSessionStats] = useState(null);
  const [sessionsList, setSessionsList] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionStatusFilter, setSessionStatusFilter] = useState('all');
  const [dropOffAnalysis, setDropOffAnalysis] = useState(null);
  const [selectedSessionsForDelete, setSelectedSessionsForDelete] = useState([]);

  // Refs that always hold the freshest values, so handlers below can be stable.
  const selectedRef = useRef([]);
  useEffect(() => { selectedRef.current = selectedSessionsForDelete; }, [selectedSessionsForDelete]);
  const filterRef = useRef('all');
  useEffect(() => { filterRef.current = sessionStatusFilter; }, [sessionStatusFilter]);

  const fetchSessionData = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const status = filterRef.current;
      const [statsData, sessionsData, dropOffData] = await Promise.all([
        sessionAPI.getStats(),
        sessionAPI.getAllSessions(status === 'all' ? null : status),
        sessionAPI.getDropOffAnalysis(),
      ]);
      setSessionStats(statsData);
      setSessionsList(sessionsData);
      setDropOffAnalysis(dropOffData);
    } catch (error) {
      console.error('Failed to load session data:', error);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  const handleDeleteSelectedSessions = useCallback(async () => {
    const ids = selectedRef.current;
    if (!ids || ids.length === 0) return;
    const ok = window.confirm(
      `Weet u zeker dat u ${ids.length} sessie${ids.length === 1 ? '' : 's'} wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`
    );
    if (!ok) return;
    try {
      await sessionAPI.bulkDeleteSessions(ids);
      setSelectedSessionsForDelete([]);
      fetchSessionData();
    } catch (error) {
      console.error('Failed to delete sessions:', error);
      alert('Fout bij verwijderen: ' + (error.response?.data?.detail || error.message || 'onbekende fout'));
    }
  }, [fetchSessionData]);

  return {
    sessionStats,
    sessionsList,
    sessionsLoading,
    sessionStatusFilter,
    setSessionStatusFilter,
    dropOffAnalysis,
    selectedSessionsForDelete,
    setSelectedSessionsForDelete,
    fetchSessionData,
    handleDeleteSelectedSessions,
  };
};
